import { useState, useEffect, useCallback } from "react"
import AlertsFilter from "./alerts/AlertsFilter"
import AlertsTable from "./alerts/AlertsTable"
import GlobalLoader from "@/components/Loading/GlobalLoader"
import styles from "../analytics.module.css"
import { api } from "@/utils/apiService"
import { useAuth } from "@/components/context/AuthContext"
import AlertDetailModal from "./alerts/AlertDetailModal"
import { toast } from "react-toastify"
import CreateAlertModal from "./alerts/CreateAlertModal"
import ConfirmDeleteAlertModal from "./user/ConfirmDeleteAlertModal"
import { useTheme } from "@/components/context/ThemeContext"

export default function AlertsManagement() {
	const {theme} = useTheme()
	const { token } = useAuth()
	const [alerts, setAlerts] = useState([])
	const [loading, setLoading] = useState(true)
	const [filters, setFilters] = useState({
		status: "active",
		severity: "",
		alertType: "",
		sortBy: "priority",
		order: "desc",
	})
	const [pagination, setPagination] = useState({
		currentPage: 1,
		totalPages: 1,
		totalItems: 0,
	})
	const [selectedAlert, setSelectedAlert] = useState(null)
	const [alertDetail, setAlertDetail] = useState(null)
	const [showCreateModal, setShowCreateModal] = useState(false)
	const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(null)

	const fetchAlertDetail = async (alertId) => {
		try {
			const res = await api.get(`/admin/analytics/alerts/${alertId}`, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			})

			if (res.data.success) {
				console.log(res.data.data);

				setAlertDetail(res.data.data)
				setSelectedAlert(alertId)
			}
		} catch (error) {
			console.error("[v0] Alert detail fetch error:", error)
		}
	}

	const handleAcknowledge = async (id) => {
		try {
			const res = await api.patch(`/admin/analytics/alerts/${id}/acknowledge`, {}, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			})

			if (res.data.success) {
				toast.success(res.data.message)
				fetchAlerts(filters, 1)
			}
		} catch (error) {
			console.error("Acknowledge error:", error)
			const mssg = error?.response?.data?.message || "Acknowledge Error"
			toast.error(mssg)
		}
	}

	const handleResolve = async (id) => {
		try {
			const res = await api.patch(`/admin/analytics/alerts/${id}/resolve`, {}, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			})

			if (res.data.success) {
				toast.success(res.data.message)
				fetchAlerts(filters, 1)
			}
		} catch (error) {
			console.error("Resolve error:", error)
			const mssg = error?.response?.data?.message || "Resolve Error"
			toast.error(mssg)
		}
	}

	const handleDelete = async (id) => {
		try {
			const res = await api.delete(`/admin/analytics/alerts/${id}`, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			})

			if (res.data.success) {
				toast.success(res.data.message)
				fetchAlerts(filters, 1)
			}
		} catch (error) {
			console.error("Deleting error:", error)
			toast.error(error?.response?.data?.message || "Error Deleting Alert")
		}
	}

	const handleCreateAlert = async (newAlert) => {
		if (!newAlert.title || !newAlert.message) {
			alert("Please fill in all required fields")
			return
		}

		try {
			const res = await api.post("/admin/analytics/alerts", newAlert, {
				headers: {
					Authorization: `Bearer ${token}`
				}
			})

			if (res.data.success) {
				setShowCreateModal(false)
				toast.success(res.data.message)
				fetchAlerts(filters, 1)
			}
		} catch (error) {
			console.error("Create alert error:", error)
			const mssg = error?.response?.data?.message || "Create alert error"
			toast.error(mssg)
		}
	}

	const fetchAlerts = useCallback(async ({ status, severity, alertType, sortBy, order }, page) => {
		setLoading(true)
		try {
			const res = await api.get("/admin/analytics/alerts", {
				headers: {
					Authorization: `Bearer ${token}`
				},
				params: {
					status,
					severity,
					alertType,
					sortBy,
					order,
					page
				}
			})

			setAlerts(res.data.data.alerts)
			setPagination(res.data.data.pagination)
		} catch (error) {
			console.error("Alerts fetch error:", error)
		} finally {
			setLoading(false)
		}
	}, [])

	useEffect(() => {
		fetchAlerts(filters, pagination.currentPage)
	}, [filters, pagination.currentPage])

	return (
		<div className={`${styles.analyticsPage} ${theme==="dark"?"dark":""}`}>
			<div className={styles.header}>
				<h1>Alerts Management</h1>
				<div className={styles.headerAction}>
					<button onClick={() => setShowCreateModal(true)} className={styles.primaryButton}>
						+ Create Alert
					</button>
				</div>
			</div>

			<AlertsFilter filters={filters} setFilters={setFilters} />

			{loading ? (
				<GlobalLoader
					isLoading={loading}
					variant="spinner"
					message="Loading Analytics Dashboard"
					fullScreen={false}
				/>
			) : (
				<>
					<AlertsTable
						alerts={alerts}
						onAcknowledge={handleAcknowledge}
						onResolve={handleResolve}
						onDelete={setShowConfirmDeleteModal}
						onViewDetail={fetchAlertDetail}
					/>

					<div className={styles.pagination}>
						<button
							disabled={pagination.currentPage === 1}
							onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage - 1 })}
						>
							Previous
						</button>
						<span>
							Page {pagination.currentPage} of {pagination.totalPages}
						</span>
						<button
							disabled={pagination.currentPage === pagination.totalPages}
							onClick={() => setPagination({ ...pagination, currentPage: pagination.currentPage + 1 })}
						>
							Next
						</button>
					</div>
				</>
			)}

			{selectedAlert && alertDetail && (
				<AlertDetailModal
					setSelectedAlert={setSelectedAlert}
					setAlertDetail={setAlertDetail}
					alertDetail={alertDetail}
				/>
			)}

			{showCreateModal && (
				<CreateAlertModal
					setShowCreateModal={setShowCreateModal}
					handleCreateAlert={handleCreateAlert}
				/>
			)}

			{showConfirmDeleteModal && (
				<ConfirmDeleteAlertModal
					isOpen={showConfirmDeleteModal}
					alert={showConfirmDeleteModal}
					onClose={() => setShowConfirmDeleteModal(null)}
					onConfirm={handleDelete}
				/>
			)}
		</div>
	)
}
